// Authentication Service

import { apiClient, setTenantCode } from './apiClient';
import {
  LoginCredentials,
  TwoFactorCredentials,
  AuthResponse,
  UserInfo,
  TenantInfo,
  TrustedDeviceInfo,
} from '../types/auth.types';

class AuthService {
  /**
   * Login - Schritt 1: Email & Password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // Device-Trust-Token aus localStorage laden und mitsenden
    const deviceToken = localStorage.getItem('deviceTrustToken');
    const payload = {
      ...credentials,
      ...(deviceToken ? { deviceToken } : {}),
    };

    const response = await apiClient.post<AuthResponse>('/auth/login', payload);

    // Wenn kein 2FA benötigt wird, Tokens speichern
    if (response.data.accessToken && response.data.refreshToken) {
      this.setTokens(response.data.accessToken, response.data.refreshToken);
    }

    return response.data;
  }

  /**
   * Login - Schritt 2: 2FA Code Verification
   */
  async verifyTwoFactor(credentials: TwoFactorCredentials): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login/2fa', credentials);

    if (response.data.accessToken && response.data.refreshToken) {
      this.setTokens(response.data.accessToken, response.data.refreshToken);
    }

    // Device-Trust-Token speichern wenn vorhanden
    if (response.data.deviceTrustToken) {
      localStorage.setItem('deviceTrustToken', response.data.deviceTrustToken);
    }

    return response.data;
  }

  /**
   * Logout - Revoke Tokens
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearTokens();
      window.location.href = '/login';
    }
  }

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<UserInfo> {
    const response = await apiClient.get<UserInfo>('/auth/me');
    return response.data;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  /**
   * Setup 2FA - Get QR Code and Secret (generates new secret)
   */
  async setup2FA(): Promise<any> {
    const response = await apiClient.post('/auth/2fa/setup');
    return response.data;
  }

  /**
   * Get existing 2FA QR Code (without resetting secret — for adding a second device)
   */
  async getExisting2FAQrCode(): Promise<any> {
    const response = await apiClient.get('/auth/2fa/setup');
    return response.data;
  }

  /**
   * Enable 2FA - Verify Code and Activate
   */
  async enable2FA(code: string): Promise<any> {
    const response = await apiClient.post('/auth/2fa/enable', { code });
    return response.data;
  }

  /**
   * Disable 2FA
   */
  async disable2FA(code: string): Promise<any> {
    const response = await apiClient.post('/auth/2fa/disable', { code });
    return response.data;
  }

  /**
   * Change Password (for authenticated users)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<any> {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  }

  /**
   * Forgot Password - Request password reset
   */
  async forgotPassword(email: string): Promise<any> {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Reset Password - Set new password with token
   */
  async resetPassword(email: string, token: string, newPassword: string): Promise<any> {
    const response = await apiClient.post('/auth/reset-password', {
      email,
      token,
      newPassword,
    });
    return response.data;
  }

  /**
   * Get user's available tenants (Vereine)
   */
  async getUserTenants(): Promise<TenantInfo[]> {
    const response = await apiClient.get<TenantInfo[]>('/auth/tenants');
    return response.data;
  }

  /**
   * Select a tenant after login (when user has multiple tenants)
   */
  async selectTenant(tenantId: string, refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/select-tenant', {
      tenantId,
      refreshToken,
    });

    if (response.data.accessToken && response.data.refreshToken) {
      this.setTokens(response.data.accessToken, response.data.refreshToken, response.data.currentTenant);
    }

    return response.data;
  }

  /**
   * Switch to a different tenant
   */
  async switchTenant(tenantId: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/switch-tenant', {
      tenantId,
    });

    if (response.data.accessToken && response.data.refreshToken) {
      this.setTokens(response.data.accessToken, response.data.refreshToken, response.data.currentTenant);
    }

    return response.data;
  }

  /**
   * Store tokens in localStorage
   */
  private setTokens(accessToken: string, refreshToken: string, tenant?: TenantInfo | null): void {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);

    // Tenant-Code speichern wenn vorhanden
    if (tenant) {
      setTenantCode(tenant.code);
      localStorage.setItem('currentTenant', JSON.stringify(tenant));
    }
  }

  /**
   * Clear all tokens and tenant info
   */
  private clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tenantCode');
    localStorage.removeItem('currentTenant');
    // deviceTrustToken wird NICHT gelöscht — er soll über Logouts hinweg bestehen,
    // damit beim nächsten Login 2FA übersprungen werden kann
  }

  /**
   * Get current tenant from localStorage
   */
  // Trusted Device Management
  async getTrustedDevices(): Promise<TrustedDeviceInfo[]> {
    const response = await apiClient.get<TrustedDeviceInfo[]>('/auth/trusted-devices');
    return response.data;
  }

  async revokeTrustedDevice(deviceId: string): Promise<void> {
    await apiClient.delete(`/auth/trusted-devices/${deviceId}`);
  }

  async revokeAllTrustedDevices(): Promise<void> {
    await apiClient.delete('/auth/trusted-devices/all');
  }

  getCurrentTenant(): TenantInfo | null {
    const tenantJson = localStorage.getItem('currentTenant');
    if (tenantJson) {
      try {
        return JSON.parse(tenantJson);
      } catch {
        return null;
      }
    }
    return null;
  }
}

export const authService = new AuthService();
